import { Alert, Link, Stack, Typography } from "@mui/material";
import Chip from "@hyro-components/Chip";
import React from "react";
import ReviewSummaryCard from "./ReviewSummaryCard";
import {
  USE_CASES,
  SERVICE_FIELDS,
  LATEST_TAG,
  NLU_MODES,
  FIELD_IDS,
  DEPLOYMENT_URL_FIELDS,
  getServicesForUseCase,
  getExtraFieldsForUseCase,
  getActiveWarnings
} from "../DeployWizard.consts";
import { WizardContainer, ReviewSection } from "../DeployWizard.styles";

const StepReview = ({ selectedUseCase, formData, latestToggles, urls, isView }) => {
  const useCase = USE_CASES[selectedUseCase];
  const { required, optional } = getServicesForUseCase(selectedUseCase);
  const allServices = [...required, ...optional];
  const useCaseExtraFields = getExtraFieldsForUseCase(selectedUseCase);
  const serviceExtraFields = allServices.flatMap((s) => s.extraFields || []);
  const extraFields = [...useCaseExtraFields, ...serviceExtraFields];
  const warnings = getActiveWarnings(selectedUseCase, formData);

  const getServiceVersion = (service) => {
    if (latestToggles[service.id]) return LATEST_TAG;
    const value = formData[service.id];
    if (value && value !== "") return value;
    return null;
  };

  const configuredServices = allServices.filter(
    (s) => getServiceVersion(s) !== null
  );
  const devServices = allServices.filter(
    (s) => getServiceVersion(s) === null
  );

  const configuredExtraFields = extraFields.filter(
    (f) => f.id !== FIELD_IDS.MEMORY && formData[f.id] !== undefined && formData[f.id] !== "" && formData[f.id] !== false
  );

  const availableUrls = urls
    ? DEPLOYMENT_URL_FIELDS.filter((f) => urls[f.id])
    : [];

  return (
    <WizardContainer>
      <Typography variant="h6" gutterBottom>
        {isView ? "Deployment Details" : "Review & Deploy"}
      </Typography>

      <Stack gap={3}>
        {warnings.map((warning) => (
          <Alert key={warning} severity="warning">
            {warning}
          </Alert>
        ))}

        <ReviewSummaryCard
          releaseName={formData.releaseName}
          ttlHours={formData.ttlHours}
          useCaseLabel={useCase?.label}
          memory={(formData[FIELD_IDS.NLU_MODE] || useCase?.nluMode) === NLU_MODES.DEDICATED ? formData.memory : null}
        />

        {availableUrls.length > 0 && (
          <ReviewSection>
            <Typography variant="subtitle2" fontWeight={600}>Deployment URLs</Typography>
            <Stack gap={1}>
              {availableUrls.map((field) => (
                <Stack key={field.id} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">{field.label}</Typography>
                  <Link
                    href={urls[field.id]}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ maxWidth: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {urls[field.id]}
                  </Link>
                </Stack>
              ))}
            </Stack>
          </ReviewSection>
        )}

        {configuredServices.length > 0 && (
          <ReviewSection>
            <Typography variant="subtitle2" fontWeight={600}>Custom Versions</Typography>
            <Stack gap={1}>
              {configuredServices.map((service) => (
                <Stack key={service.serviceKey} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">{service.label}</Typography>
                  <Chip
                    size="small"
                    sx={{ fontSize: "12px" }}
                    label={getServiceVersion(service)}
                    backgroundColorPath={["tertiary", "100"]}
                    textColorPath={["text", "800"]}
                  />
                </Stack>
              ))}
            </Stack>
          </ReviewSection>
        )}

        {devServices.length > 0 && (
          <ReviewSection>
            <Typography variant="subtitle2" fontWeight={600}>Using Dev Defaults</Typography>
            <Stack gap={1}>
              {devServices.map((service) => (
                <Stack key={service.serviceKey} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">{service.label}</Typography>
                  <Chip
                    size="small"
                    sx={{ fontSize: "12px" }}
                    label="dev"
                    backgroundColorPath={["grey", "200"]}
                    textColorPath={["text", "600"]}
                  />
                </Stack>
              ))}
            </Stack>
          </ReviewSection>
        )}

        {configuredExtraFields.length > 0 && (
          <ReviewSection>
            <Typography variant="subtitle2" fontWeight={600}>Additional Settings</Typography>
            <Stack gap={1}>
              {configuredExtraFields.map((field) => (
                <Stack key={field.id} direction="row" justifyContent="space-between">
                  <Typography variant="body2">{field.label}</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {typeof formData[field.id] === "boolean" ? (formData[field.id] ? "Enabled" : "Disabled") : formData[field.id]}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </ReviewSection>
        )}

      </Stack>
    </WizardContainer>
  );
};

export default StepReview;
